import './SeatPlacePicker.styles.scss';

import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import React, { FC, useEffect, useRef, useState } from 'react';

import { useOverlayClickOutside } from '../../../hooks/useOverlayClickOutside';
import { SeatPlacePickerProps } from './SeatPlacePicker.types';

const SEAT_MIN = 0;

const SeatPlacePicker: FC<SeatPlacePickerProps> = ({
  value,
  defaultPlace,
  seatCount,
  disabled,
  onChange,
  onClear,
}) => {
  const overlayRef = useRef<OverlayPanel>(null);
  const triggerRef = useRef<HTMLInputElement>(null);
  const [seatMaxValue, setSeatMaxValue] = useState<number>(0);

  useEffect(() => {
    setSeatMaxValue(seatCount || 0);
  }, [seatCount]);

  useOverlayClickOutside(overlayRef, triggerRef);

  const getNextSeat = (seat: number): number =>
    Math.min(seatMaxValue, Math.max(SEAT_MIN, seat));

  const getCurrentSeat = (): number => {
    if (value != null) {
      return getNextSeat(value);
    }
    if (defaultPlace != null) {
      return getNextSeat(defaultPlace);
    }
    return SEAT_MIN;
  };

  const openPanel = (e: React.MouseEvent<HTMLInputElement>): void =>
    overlayRef.current?.show(e, e.currentTarget);

  const increment = (): void => {
    const current = getCurrentSeat();
    onChange(current >= seatMaxValue ? SEAT_MIN : current + 1);
  };

  const decrement = (): void => {
    const current = getCurrentSeat();
    onChange(current <= SEAT_MIN ? seatMaxValue : current - 1);
  };

  const handleClear = (): void => {
    onClear();
    overlayRef.current?.hide();
  };

  return (
    <div className="SeatPlacePicker-Component col-1 pe-0">
      <InputText
        ref={triggerRef}
        className="seat-place-picker-input text-center w-100"
        placeholder="Place"
        value={value != null ? String(value) : ''}
        readOnly
        onMouseDown={openPanel}
        disabled={disabled}
      />
      <OverlayPanel
        ref={overlayRef}
        className="SeatPlacePicker-panel"
        dismissable
        data-ignore-outside-click
      >
        <div className="p-datepicker p-datepicker-timeonly seat-place-picker-datepicker">
          <div className="p-timepicker">
            <div className="p-hour-picker">
              <Button
                type="button"
                className="p-link"
                onClick={increment}
                aria-label="Increase seat"
              >
                <span className="p-button-icon pi pi-chevron-up" />
              </Button>
              <span>{getCurrentSeat()}</span>
              <Button
                type="button"
                className="p-link"
                onClick={decrement}
                aria-label="Decrease seat"
              >
                <span className="p-button-icon pi pi-chevron-down" />
              </Button>
            </div>
          </div>
          <div className="p-datepicker-buttonbar">
            <Button label="Clear" text plain onClick={handleClear} />
          </div>
        </div>
      </OverlayPanel>
    </div>
  );
};

export default SeatPlacePicker;
